import React, { useState, useEffect, useMemo } from "react";
import { Polyline } from "react-leaflet";

import "leaflet-polylineoffset";
import "leaflet.polylinedecorator";

import MapConfig from "services/map/MapConfig";
import MapTooltipLabel from "components/MapTooltipLabel";

type Props = {
  label?: string | JSX.Element | null;
  disabled: boolean;
  onClick: Function;
  positions: [];
  selected: boolean;
  zoom: number;
  defaultZoom: number;
  options: {
    background?: {};
    withLabel?: boolean;
    withLabelBackground?: boolean;
    stroke?: number;
    offset?: number;
    arrow_display?: boolean;
    disabled?: boolean;
    unselected_color?: string;
    selected_color?: string;
    disabled_color?: string;
    tooltipCoordinates?: [];
    arrow?: {
      display?: boolean;
      endOffset?: string;
      offset?: string;
      repeat?: string;
      pixel_size?: number;
      unselected_color?: string;
      selected_color?: string;
      disabled_color?: string;
    };
  };
  interactive?: boolean;
};

function MapPath({
  label,
  defaultZoom,
  disabled,
  onClick,
  positions,
  selected,
  zoom,
  options,
  interactive = true,
}: Props): JSX.Element {
  const [leafletInstance, setLeafletInstance] = useState<{
    leafletElement: { _map: any; getBounds: Function };
  }>();

  const computedSelected = useMemo(() => {
    return !disabled && selected;
  }, [disabled, selected]);

  const color = useMemo(() => {
    if (disabled) {
      return options?.disabled_color || MapConfig?.flow?.path?.color?.disabled;
    }

    const unselectedColor =
      options?.unselected_color || MapConfig?.flow?.path?.color?.unselected;
    const selectedColor =
      options?.selected_color || MapConfig?.flow?.path?.color?.selected;

    return selected ? selectedColor : unselectedColor;
  }, [
    disabled,
    selected,
    options?.disabled_color,
    options?.unselected_color,
    options?.selected_color,
  ]);

  const arrowFillColor = useMemo(() => {
    if (disabled) {
      return (
        options?.arrow?.disabled_color ||
        MapConfig?.flow?.path?.arrow?.color?.disabled
      );
    }

    const unselectedColor =
      options?.arrow?.unselected_color ||
      MapConfig?.flow?.path?.arrow?.color?.unselected;
    const selectedColor =
      options?.arrow?.selected_color ||
      MapConfig?.flow?.path?.arrow?.color?.selected;

    return selected ? selectedColor : unselectedColor;
  }, [disabled, options?.arrow, selected]);

  const stroke = useMemo(() => {
    const defaultStroke = MapConfig?.flow?.path?.stroke?.default || 4;
    const initialStroke =
      options?.stroke ||
      MapConfig?.flow?.path?.stroke?.[`zoom${zoom}`] ||
      defaultStroke;

    if (zoom >= defaultZoom) {
      return initialStroke;
    }

    return initialStroke - (defaultZoom - zoom) * 3;
  }, [options, defaultZoom, zoom]);

  const arrowPixelSize = useMemo(() => {
    const initialPixelSize =
      options?.arrow?.pixel_size ||
      MapConfig?.flow?.path?.arrow?.pixelSize ||
      0;

    if (zoom >= defaultZoom) {
      return initialPixelSize;
    }

    return (
      initialPixelSize -
      (defaultZoom - zoom) * 4 * Math.floor(initialPixelSize / 10)
    );
  }, [zoom, options?.arrow?.pixel_size, defaultZoom]);

  /* eslint-disable no-undef */

  /* eslint-disable no-undef */
  const leafletArrow = useMemo(() => {
    // @ts-ignore
    if (L?.polylineDecorator && options?.arrow?.display) {
      // @ts-ignore
      return L?.polylineDecorator(leafletInstance?.leafletElement, {
        patterns: [
          {
            offset:
              options?.arrow?.offset || MapConfig?.flow?.path?.arrow?.offset,
            endOffset:
              options?.arrow?.endOffset ||
              MapConfig?.flow?.path?.arrow?.endOffset,
            repeat:
              options?.arrow?.repeat || MapConfig?.flow?.path?.arrow?.repeat,
            lineOffset: options?.offset || 0,
            // @ts-ignore
            symbol: L.Symbol.arrowHead({
              pixelSize: arrowPixelSize,
              pathOptions: {
                opacity: 1,
                fillOpacity: 1,
                weight: 0,
                fillColor: arrowFillColor,
                decorator: true,
                interactive,
              },
            }),
          },
        ],
      });
    }
  }, [
    options?.arrow?.display,
    options?.arrow?.offset,
    options?.arrow?.endOffset,
    options?.arrow?.repeat,
    options?.offset,
    leafletInstance?.leafletElement,
    arrowPixelSize,
    arrowFillColor,
    interactive,
  ]);
  /* eslint-enable no-undef */

  useEffect(() => {
    if (
      options?.arrow?.display &&
      leafletArrow &&
      leafletInstance?.leafletElement
    ) {
      const leafletMap = leafletInstance?.leafletElement?._map;
      leafletArrow?.addTo(leafletMap);

      return () => {
        leafletArrow?.removeFrom(leafletMap);
      };
    }
  }, [options?.arrow?.display, leafletInstance, leafletArrow]);

  // For Path, we use a position for the toolTip in lat/lng without offset
  const tooltipPosition = useMemo(() => {
    if (!options?.tooltipCoordinates) {
      return leafletInstance?.leafletElement?.getBounds()?.getCenter();
    }

    return options?.tooltipCoordinates;
  }, [leafletInstance?.leafletElement, options?.tooltipCoordinates]);

  function handleOnClick() {
    if (disabled) return null;
    return onClick();
  }

  return (
    <>
      <Polyline
        ref={(ref: any) => {
          setLeafletInstance(ref);
        }}
        fill={false}
        positions={positions}
        opacity={1}
        weight={stroke}
        color={color}
        onClick={handleOnClick}
        offset={options?.offset}
        interactive={interactive}
      />
      {options?.withLabel && label ? (
        <MapTooltipLabel
          type="path"
          label={label}
          position={tooltipPosition}
          selected={computedSelected}
          background={
            options?.withLabelBackground ? options?.background : undefined
          }
          disabled={disabled}
          onClick={onClick}
          interactive={interactive}
        />
      ) : null}
    </>
  );
}

export default MapPath;
